// Biometric authentication bridge
// Native (Capacitor): uses @aparajita/capacitor-biometric-auth for real FaceID/TouchID
// Web (PWA): falls through to existing WebAuthn code in App.js

import { isNative } from './capacitor';

export async function isBiometricAvailable() {
  if (isNative()) {
    try {
      const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
      const result = await BiometricAuth.checkBiometry();
      return result.isAvailable;
    } catch {
      return false;
    }
  }
  // Web fallback: check WebAuthn platform authenticator
  if (typeof window.PublicKeyCredential === 'undefined') return false;
  if (!window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Prompt for biometric auth.
 * @param {string} reason — copy shown in the native prompt (default: "Unlock Sirat").
 * @returns {{ok: boolean, reason: 'success'|'cancel'|'fail'|'unavailable'}} — tells the caller
 *   *why* auth ended, so it can distinguish "user cancelled" (keep the gate up) from
 *   "biometrics aren't available on this device" (gate can't work, fail-open).
 */
export async function authenticateBiometric(reason = 'Unlock Sirat') {
  if (isNative()) {
    try {
      const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
      // Re-check availability so we can return a distinct 'unavailable' signal
      const avail = await BiometricAuth.checkBiometry();
      if (!avail?.isAvailable) return { ok: false, reason: 'unavailable' };
      await BiometricAuth.authenticate({
        reason,
        cancelTitle: 'Cancel',
        // iOS: when the user fails biometrics enough times, the OS can fall back
        // to the device passcode. Setting this enables the deviceOwnerAuthentication
        // policy and customises the fallback button label.
        iosFallbackTitle: 'Use Passcode',
        // Android: allow device credential (PIN/pattern/password) fallback.
        allowDeviceCredential: true,
      });
      return { ok: true, reason: 'success' };
    } catch (err) {
      // Plugin throws a BiometryError on cancel / failure. Treat anything other
      // than a true "device has no biometry" as a failed attempt so the caller
      // can keep the lock screen up.
      const code = err?.code || err?.message || '';
      const isUnavailable = /biometryNotAvailable|notEnrolled|noneEnrolled|passcodeNotSet/i.test(String(code));
      return { ok: false, reason: isUnavailable ? 'unavailable' : 'fail' };
    }
  }
  // Web: return false — caller uses existing WebAuthn flow in App.js
  return { ok: false, reason: 'unavailable' };
}
